<svelte:options customElement="hb-input-radio" />

<script lang="ts">
  /**
   * Svelte Input Radio WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type { Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let {
    schemaentry = undefined,
    show_validation = "no",
  }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  let value: string | undefined = $state(undefined);
  let options: { value: string; label?: string }[] = $derived(
    field?.params?.options ?? [],
  );

  let valid = $derived.by(() => {
    if (field == null) return false;
    if (field.required) return !!value;
    return true;
  });

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      if (nextField.value !== undefined && nextField.value !== null) {
        value = String(nextField.value);
      } else {
        value = undefined;
      }
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });
</script>

{#if field != null}
  <div class="field">
    {#if options.length}
      <div class="control">
        <div
          class="radios"
          role="radiogroup"
          aria-label={field?.label ?? field?.id ?? "Options"}
        >
          {#each options as option (option.value)}
            <label class="radio">
              <input
                type="radio"
                part="input"
                name={field?.id}
                value={option.value}
                bind:group={value}
                required={field?.required}
                disabled={!!field?.readonly || !!field?.disabled}
              />
              {option.label ?? option.value}
            </label>
          {/each}
        </div>
      </div>
    {/if}

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
